import {
  Configuration,
  InvoiceTypeEnum,
  ServiceTypeEnum,
  StatementStatusEnum,
  StatementsApi,
} from '@sr-sdks/api-sdk-axios';
import {
  keepPreviousData,
  queryOptions,
  skipToken,
  useQuery,
} from '@tanstack/react-query';
import type { RawAxiosRequestConfig } from 'axios';
import { configuration } from '../../configuration';
import axiosInstance, { bypassCacheConfig } from '../../utils/axiosInstance';
import { toApiError } from '../../utils/httpError';

export const STATEMENTS_DEFAULT_PARAMS = {
  page: 1,
  pageSize: 10,
  sortBy: 'statementDate:desc',
} as const satisfies StatementsQueryParams;

// ===== API CLIENT =====

/**
 * Create a statements API instance
 */
const getStatementsApi = () => {
  return new StatementsApi(
    new Configuration(),
    configuration.PAYMENTS_API_BASE,
    axiosInstance,
  );
};

// ===== QUERY KEYS =====

/**
 * @internal **IMPORTANT**: Avoid exporting and prefer to use
 * a Query Options factory instead.
 */
const statementsKeys = {
  all: ['statements'] as const,
  paginated: (params: StatementsQueryParams) =>
    [...statementsKeys.all, 'paginated', { ...params }] as const,
};

// ===== API FUNCTIONS =====

export interface StatementsQueryParams {
  beneficiaryClientId?: string;
  beneficiarySerialTitleId?: string;
  beneficiaryTitleId?: string;
  beneficiaryWorkspaceId?: string;
  invoiceId?: string;
  invoiceType?: InvoiceTypeEnum;
  ownerWorkspaceId?: string;
  page?: number;
  pageSize?: number;
  searchTerm?: string;
  serviceId?: string;
  serviceType?: ServiceTypeEnum[];
  sortBy?: string;
  statementNo?: string;
  status?: StatementStatusEnum;
}

/**
 * Fetch statements with optional filters
 */
export const fetchStatements = async (
  params: StatementsQueryParams,
  options?: RawAxiosRequestConfig,
) => {
  try {
    // Argument order must match the regenerated SDK signature.
    const response = await getStatementsApi().statementControllerFindAll(
      params?.beneficiaryClientId,
      params?.beneficiarySerialTitleId,
      params?.beneficiaryTitleId,
      params?.beneficiaryWorkspaceId,
      params?.invoiceId,
      params?.invoiceType,
      params?.ownerWorkspaceId,
      params?.searchTerm,
      params?.serviceId,
      params?.serviceType,
      params?.statementNo,
      params?.status,
      params?.page,
      params?.pageSize,
      params?.sortBy,
      { ...bypassCacheConfig(), ...options },
    );

    return response.data;
  } catch (error) {
    // Do NOT capture Sentry exceptions here; QueryCache.onError already captures.
    throw toApiError(error, {
      endpoint: 'statementControllerFindAll',
      params: params,
    });
  }
};

// ===== QUERY OPTIONS =====

/**
 * React Query options for a paginated statements list.
 * Skips the fetch when `params` is undefined (caller opt-out).
 */
export const queryOptionsGetStatementsPaginated = (
  params?: StatementsQueryParams,
) => {
  const queryParams = {
    ...STATEMENTS_DEFAULT_PARAMS,
    ...params,
  };

  return queryOptions({
    queryFn: params ? () => fetchStatements(queryParams) : skipToken,
    queryKey: statementsKeys.paginated(queryParams),
  });
};

// ===== QUERY HOOKS =====

/** Narrow opt-in flags for {@link useStatements} call sites. */
export type UseStatementsOptions = {
  enabled?: boolean;
  placeholderData?: typeof keepPreviousData;
};

/**
 * Hook for fetching statements with optional filters.
 * `options` is intentionally narrow so call sites opt into behaviour like
 * `placeholderData: keepPreviousData` without affecting every consumer.
 */
export const useStatements = (
  params?: StatementsQueryParams,
  options?: UseStatementsOptions,
) => {
  return useQuery({
    ...queryOptionsGetStatementsPaginated(params),
    ...options,
  });
};
